import { faHashtag, faLock, faPen, faToolbox, faTvRetro, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { pick } from 'lodash';
import React, { useRef } from 'react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';

import { crewRoleTexts } from '../../../common/brickfilms';
import { getContentLanguagesValues, getCurrentInterfaceLanguage } from '../../../contexts/LanguageContext';
import Button from '../../Atoms/Button';
import ButtonsContainer from '../../Atoms/ButtonsContainer';
import Card from '../../Atoms/Card';
import FormGrid from '../../Atoms/FormGrid';
import FormGroup from '../../Atoms/FormGroup';
import Heading from '../../Atoms/Heading';
import Input from '../../Atoms/Input';
import { LinkInputList } from '../../Atoms/LinkInput';
import List from '../../Atoms/List';
import Select from '../../Atoms/Select';
import Switch from '../../Atoms/Switch';
import UploadInput from '../../Atoms/UploadInput';
import Editor from '../../BBcode/Editor';
import SideViewContainer from '../../Layout/SideViewContainer';
import { ContentsForm } from '../ContentsForm';
import { TeamForm } from '../TeamForm';

const POSITION_BUTTON = 'RIGHT';

const BrickfilmForm = ({
  t,
  brickfilm = {
    title: '',
    type: 'FILM',
    description: '',
    language: getCurrentInterfaceLanguage(),
    isAbandoned: false,
    isArchived: false,
    isRecommended: false,
    isFilmOfTheWeek: false,
    isFilmOfTheMonth: false,
    isSponsored: false,
    allowedToReuse: true,
    containsExplicitContent: false,
    picturePath: '',
  },
  onSubmit = () => { },
  loading = false,
  buttonLabel,
}) => {
  const form = useForm({
    mode: 'all',
    defaultValues: {
      ...pick(brickfilm, [
        'title',
        'type',
        'description',
        'language',
        'isAbandoned',
        'isRecommended',
        'isFilmOfTheWeek',
        'isFilmOfTheMonth',
        'isSponsored',
        'allowedToReuse',
        'containsExplicitContent',
        'picturePath',
        'videoLinks',
        'teaserLinks',
        'isArchived',
        'includesPeter',
      ]),
      type: brickfilm?.type?.replaceAll('PROJECT', 'FILM'),
      makingOf: brickfilm?.makingOf?.value,
      castMembers: brickfilm?.castMembers.map(e => ({
        id: e.id,
        user: {
          member: e.member,
          memberName: e.memberName
        },
        role: e.role,
        additionnalInformation: e.additionnalInformation
      })),
      productionNotes: brickfilm?.productionNotes?.map(e => ({
        id: e.id,
        content: e.content.value,
        createdAt: e.createdAt,
      }))
    },
  });

  const makingOfEditorRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingContents, setIsUploadingContents] = useState(false);

  const [categorySelectedIndex, setCategorySelected] = useState(0);

  const {
    watch,
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
  } = form;

  const showAdmin = ['EDIT_BRICKFILM_OF_THE_MONTH', 'EDIT_BRICKFILM_OF_THE_WEEK', 'EDIT_RECOMMENDED_BRICKFILM', 'EDIT_SPONSORED_BRICKFILM', 'EDIT_PETER_STATUS'].some(action => brickfilm.allowedActions.includes(action));

  const TYPE_OPTIONS = [
    {
      value: 'FILM',
      label: t('Brickfilm'),
    },
    {
      value: 'TEST',
      label: t('Animation test'),
    },
  ];

  const crewRoles = crewRoleTexts(t)
  const TEAM_ROLE_OPTIONS = Object.keys(crewRoles).map(value => ({ value, label: crewRoles[value] }));

  const required = t('Required field');

  const validatorForm = {
    title: register('title', { required, minLength: { value: 3, message: t('The title need to be at least 3 characters long') } }),
    type: register('type', { required }),
    language: register('language', { required }),
    description: register('description'),
    isAbandoned: register('isAbandoned'),
    isRecommended: register('isRecommended'),
    isFilmOfTheWeek: register('isFilmOfTheWeek'),
    isFilmOfTheMonth: register('isFilmOfTheMonth'),
    isArchived: register('isArchived'),
    isSponsored: register('isSponsored'),
    allowedToReuse: register('allowedToReuse'),
    containsExplicitContent: register('containsExplicitContent'),
    picturePath: register('picturePath'),
    videoLinks: register('videoLinks'),
    teaserLinks: register('teaserLinks'),
    makingOf: register('makingOf'),
    castMembers: register('castMembers'),
    productionNotes: register('productionNotes'),
    includesPeter: register('includesPeter'),
  };

  const generalForm = <>
    <Heading h={1}>{t('Edit general information')}</Heading>

    <FormGrid>
      <FormGroup label={t('Title')} id="title" error={errors.title} required>
        <Input id="title" register={validatorForm.title} />
      </FormGroup>
      <FormGroup label={t('Language')} id="language" error={errors.language} required>
        <Select control={control} options={getContentLanguagesValues()} register={validatorForm.language} />
      </FormGroup>
      <FormGroup label={t('Type')} id="type" error={errors.type} required>
        <Select control={control} options={TYPE_OPTIONS} register={validatorForm.type} />
      </FormGroup>
      <FormGroup label={t('This brickfilm is private')} id="isArchived" error={errors.isArchived}>
        <Switch id="isArchived" register={validatorForm.isArchived} />
      </FormGroup>
    </FormGrid>

    <FormGroup label={t('Description')} id="description" error={errors.description} required>
      <Input type="TEXT" id="description" register={validatorForm.description} />
    </FormGroup>

    <FormGrid>
      <FormGroup label={t('This brickfilm is abandoned')} id="isAbandoned" error={errors.isAbandoned}>
        <Switch id="isAbandoned" register={validatorForm.isAbandoned} />
      </FormGroup>
      <FormGroup label={watch('type') === 'FILM' ? t('Allow Brickfilms.com to use this film during events or for the site promotion') : t('Allow Brickfilms.com to use this animation test during events or for the site promotion')} id="allowedToReuse" error={errors.allowedToReuse}>
        <Switch id="allowedToReuse" register={validatorForm.allowedToReuse} />
      </FormGroup>
    </FormGrid>

    <FormGroup label={t('Thumbnail')}>
      <UploadInput control={control} register={validatorForm.picturePath} type="THUMBNAIL_BRICKFILM" />
    </FormGroup>
  </>

  const adminForm = <>
    <Heading h={1}>{t('Admin area')}</Heading>
    <FormGrid>
      {brickfilm.allowedActions.includes('EDIT_RECOMMENDED_BRICKFILM') && <FormGroup label={t('Recommended (Can send a notification)')} id="isRecommended" error={errors.isRecommended}>
        <Switch id="isRecommended" register={validatorForm.isRecommended} />
      </FormGroup>}
      {brickfilm.allowedActions.includes('EDIT_BRICKFILM_OF_THE_WEEK') && <FormGroup label={t('Brickfilm of the week (Can send a notification)')} id="isFilmOfTheWeek" error={errors.isFilmOfTheWeek}>
        <Switch id="isFilmOfTheWeek" register={validatorForm.isFilmOfTheWeek} />
      </FormGroup>}
      {brickfilm.allowedActions.includes('EDIT_BRICKFILM_OF_THE_MONTH') && <FormGroup label={t('Brickfilm of the month (Can send a notification)')} id="isFilmOfTheMonth" error={errors.isFilmOfTheMonth}>
        <Switch id="isFilmOfTheMonth" register={validatorForm.isFilmOfTheMonth} />
      </FormGroup>}
      {brickfilm.allowedActions.includes('EDIT_SPONSORED_BRICKFILM') && <FormGroup label={t('Sponsored by LEGO')} id="isSponsored" error={errors.isSponsored}>
        <Switch id="isSponsored" register={validatorForm.isSponsored} />
      </FormGroup>}
      {brickfilm.allowedActions.includes('EDIT_PETER_STATUS') && <FormGroup label={t('This brickfilm includes Peter Valerius')} id="includesPeter" error={errors.includesPeter}>
        <Switch id="includesPeter" register={validatorForm.includesPeter} />
      </FormGroup>}
    </FormGrid>
  </>

  const makingOfForm = <>
    <Heading h={1}>{t('Edit the making-of')}</Heading>
    <FormGroup label={t('Making-of')} id="makingOf" error={errors.makingOf}>
      <Editor ref={makingOfEditorRef} control={control} placeholder={t('Write your making-of…')} t={t} register={validatorForm.makingOf} onUploadStatusChange={setIsUploading}/>
    </FormGroup>
  </>

  const watchForm = <>
    <Heading h={1}>{t('Edit the broadcast')}</Heading>

    <FormGroup label={t('Brickfilm links')} id="videoLinks" error={errors.videoLinks}>
      <LinkInputList name="videoLinks" />
    </FormGroup>

    <FormGroup label={t('Teaser links')} id="teaserLinks" error={errors.teaserLinks}>
      <LinkInputList name="teaserLinks" />
    </FormGroup>

    <FormGrid>
      <FormGroup label={t('This film contains content not suitable for a young audience')} id="containsExplicitContent" error={errors.containsExplicitContent}>
        <Switch id="containsExplicitContent" register={validatorForm.containsExplicitContent} />
      </FormGroup>
    </FormGrid>
  </>

  const productionForm = <>
    <Heading h={1}>{t('Edit production notes')}</Heading>
    <ContentsForm
      name="productionNotes"
      order="DESC"
      tAdd={t('Add the note')}
      tRemove={t('Remove this note')}
      tPlaceholder={t('Write your production note…')}
      hideInput
      onUploadStatusChange={setIsUploadingContents}
    />
  </>

  const castForm = <>
    <Heading h={1}>{t('Edit the crew team')}</Heading>
    <FormGroup label={t('Crew team')} id="castMembers" error={errors.castMembers}>
      <TeamForm control={control} roles={TEAM_ROLE_OPTIONS} name="castMembers" />
    </FormGroup>

  </>

  const categoryForms = [
    {
      id: 'general',
      title: t('General'),
      color: '',
      icon: faHashtag,
      selected: true,
      form: generalForm,
    },
    {
      id: 'production',
      title: t('Production'),
      color: '',
      icon: faPen,
      form: productionForm,
    },
    {
      id: 'makingof',
      title: t('Making-of'),
      color: '',
      icon: faToolbox,
      form: makingOfForm,
    },
    {
      id: 'crewteam',
      title: t('Crew team'),
      color: '',
      icon: faUserGroup,
      form: castForm,
    },
    {
      id: 'watch',
      title: t('Watch'),
      color: '',
      icon: faTvRetro,
      form: watchForm,
    },
    ...(showAdmin ? [{
      id: 'admin',
      title: t('Admin'),
      color: '',
      icon: faLock,
      form: adminForm,
    }] : []),
  ];

  const category = categoryForms[categorySelectedIndex];

  return (
    <SideViewContainer sideSize="SMALL" sideChildren={<List selectedIndex={categorySelectedIndex} list={categoryForms} onChangeSelected={(selected, index) => setCategorySelected(index)} />} reverse>
      <Card>
        <FormProvider {...form}>
          <form id="brickfilm">
            <React.Fragment key={category.id}>
              {category?.form}
            </React.Fragment>
            <ButtonsContainer position={POSITION_BUTTON}>
              <Button action={handleSubmit(onSubmit)} disabled={!isDirty || !isValid || isUploading || isUploadingContents} loading={loading} type="submit" form="brickfilm" label={buttonLabel} />
            </ButtonsContainer>
          </form>
        </FormProvider>
      </Card>
    </SideViewContainer>
  );
};

export default withTranslation()(BrickfilmForm);
